import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useB2C } from '@zet/common/hooks/useB2C'
import { useAgentDetailsWF } from '@zet/customer'
import { zetApi } from '@zet/network'
import { isCustomerFlow } from '@zet/utils/isCustomerFlow'
import { jfsUserIds } from '@zet/zet-pro-quick-journey/utils/constants'

import useBrandStore from '../store/brands'
import useFilterStore from './useFilterStore'

type IBenefit = {
  tag: string
  icon: string
  heading: string
  subText: Array<string>
}

export type IProduct = {
  id: number
  name: string
  crossSellBanner?: string
  partnerId: number
  groupCode: number
  groupName: string
  imageUrl: string
  remainingCount: number
  backgroundGradient: Array<string>
  crossSellBackGround?: string
  crossSellTitleHi?: string
  crossSellTitleEn?: string
  isOnHold: false
  rejectionPayout: string
  payout: {
    amount: number
    type: 'flat' | 'percentage'
  }
  benefits: Array<IBenefit>
  joiningFees: string
  renewalFees: string
  voucher: string
  newBrand: boolean
  topProducts: Array<IProduct>
  eligibility: {
    isEligible: boolean
    reason: string
    criteria: string
  }
  categoryId: number
}

export type OtherCategorie = {
  categoryId: string
  categoryName: string
  totalCount: number
}

export type CustomerLead = {
  partnerId: string
  status: 'leadadded'
}

export type BrandListingPageData = {
  data: {
    isCategoryInEligible?: boolean
    totalProductCount?: number
    categoryId: number
    categoryName: string
    customerLeads: Array<CustomerLead>
    product?: IProduct
    products: Array<IProduct>
    topProducts: Array<IProduct>
    otherCategoryProducts?: Array<OtherCategorie>
    eligibleCardsCountExcludingMagnet?: number
    paginationMeta: {
      pageNum: number
      pageSize: number
      startRow: number
      endRow: number
      pages: number
      nextPage: number
      hasPreviousPage: false
      hasNextPage: true
      navigateFirstPage: number
      navigateLastPage: number
      total: number
      firstPage: boolean
      lastPage: boolean
    }
  }
}

type BrandRecommendationsProps = {
  categoryId?: string | number
  variantId?: string | number | null
  isDemoSale?: boolean
  customerId?: string | number
}

type GetPreApprovedLoansProps = {
  categoryId?: string | number
  variantId?: string | number | null
  isDemoSale?: boolean
  customerId?: string | number
  enabled?: boolean
}

export const BRAND_RECOMMENDATION_API_LIMIT = 3
export const BRAND_RECOMMENDATION_API_LIMIT_HIGHER_END = 1000

export const useBrandRecommendations = ({
  categoryId,
  customerId,
  variantId,
}: BrandRecommendationsProps) => {
  const pincode = useBrandStore((state) => state?.userPincode)

  const selectedFiltersTransformed = useFilterStore(
    (state) => state?.selectedFiltersTransformed,
  )
  const filters = useFilterStore((state) => [
    state?.categoryId,
    state?.subFilters,
    state?.quickFilter,
    state?.sort,
    state?.showVariants,
  ])

  const categoryIdFilter = selectedFiltersTransformed?.categoryId || categoryId

  const { showIneligible: originalShowIneligible, hasMagnetCard } = useB2C()
  const { data: agentData } = useAgentDetailsWF()

  const onecodeId = Number(agentData?.onecodeId)
  const isJfsUser = jfsUserIds.includes(onecodeId)

  // Override showIneligible to false for jfsUser
  const showIneligible = isJfsUser ? false : originalShowIneligible

  const queryKey = [
    'BrandListing',
    categoryIdFilter,
    pincode,
    filters,
    customerId ?? 'no-customer',
    variantId,
    hasMagnetCard,
  ]

  return useInfiniteQuery<BrandListingPageData>({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      fetchBrandListing({
        pageParam,
        customerId,
        pincode,
        variantId,
        selectedFiltersTransformed,
        categoryIdFilter,
        showIneligible,
        hasMagnetCard,
      }),

    getNextPageParam: (lastpage) => {
      const paginationMeta = lastpage?.data?.paginationMeta
      if (!paginationMeta?.hasNextPage) {
        return undefined
      }

      return paginationMeta?.nextPage
    },
  })
}

const fetchBrandListing = async ({
  pageParam = 1,
  customerId,
  pincode,
  variantId,
  selectedFiltersTransformed,
  categoryIdFilter,
  showIneligible,
  hasMagnetCard,
}: {
  pageParam: number
  customerId: string | number | undefined
  pincode: string
  variantId: string | number | null | undefined
  selectedFiltersTransformed: Record<string, string | number | Array<string>>
  categoryIdFilter: string | number | string[] | undefined
  showIneligible: boolean
  hasMagnetCard?: boolean
}) => {
  const res = await zetApi.get(
    customerId ? '/variants/recommendation' : '/variants/products',
    {
      params: {
        customerId,
        pincode,
        variantId,
        ...selectedFiltersTransformed,
        categoryId: categoryIdFilter,
        limit:
          (selectedFiltersTransformed.limit ?? pageParam > 1)
            ? BRAND_RECOMMENDATION_API_LIMIT_HIGHER_END
            : customerId
              ? BRAND_RECOMMENDATION_API_LIMIT
              : BRAND_RECOMMENDATION_API_LIMIT_HIGHER_END,
        page: 1,
        showIneligible: showIneligible || !isCustomerFlow(),
        showMagnetCard: !hasMagnetCard,
      },
    },
  )
  return res.data
}

export const useGetPreApprovedLoans = ({
  customerId,
  variantId,
  enabled = true,
}: GetPreApprovedLoansProps) => {
  const selectedFiltersTransformed = useFilterStore(
    (state) => state?.selectedFiltersTransformed,
  )

  const filters = useFilterStore((state) => [
    state?.categoryId,
    state?.subFilters,
    state?.quickFilter,
    state?.sort,
    state?.showVariants,
  ])

  const queryKey = [
    'BrandListing',
    filters,
    customerId ?? 'no-customer',
    variantId,
  ]
  return useQuery({
    queryFn: () =>
      fetchPreApprovedLoans({
        customerId,
        selectedFiltersTransformed,
      }),
    queryKey,
    enabled,
  })
}

const fetchPreApprovedLoans = async ({
  customerId,
  selectedFiltersTransformed,
}: {
  customerId: string | number | undefined
  selectedFiltersTransformed: Record<string, string | number | Array<string>>
}) => {
  const filters = { ...selectedFiltersTransformed }
  delete filters?.categoryId
  const res = await zetApi.get('/preapproved/recommendation', {
    params: {
      customerId,
      ...filters,
    },
  })

  return res.data?.data
}
